/**
 * Admin formatting utilities.
 *
 * The locale-aware date / currency / number formatters now live in the shared
 * module (`@/shared/lib/formatting`) so the admin and consumer apps share one
 * implementation. They are re-exported here so the existing
 * `@/admin/lib/formatting` import path keeps working. In components, prefer the
 * `useFormatters()` hook (or its alias `useFormatting()`), which binds these to
 * the org's locale/currency automatically.
 *
 * The record-ID and duration helpers below are admin-domain, locale-agnostic
 * string builders and stay here.
 */

// ── Locale-aware formatters (single source of truth in shared) ──────────

export {
  countryToLocale,
  isMoneyObject,
  formatCurrencyValue,
  getCurrencySymbol,
  formatCurrency,
  formatCurrencyCompact,
  formatDate,
  formatDateLong,
  formatDateTime,
  formatTime,
  formatNumber,
} from '@/shared/lib/formatting'

// ── Durations ────────────────────────────────────────────

/**
 * Format a duration given in months. Durations of a whole year or more are
 * shown as "N year(s)" plus any remaining months ("N year(s) M month(s)");
 * anything under a year stays in months. Mirrors the backend
 * `WarrantyTemplateOption.format_duration` exactly so the admin UI and the
 * API-rendered `display_name` never disagree.
 * e.g. formatDurationFromMonths(12) → '1 year'
 *      formatDurationFromMonths(18) → '1 year 6 months'
 *      formatDurationFromMonths(24) → '2 years'
 *      formatDurationFromMonths(32) → '2 years 8 months'
 *      formatDurationFromMonths(11) → '11 months'
 */
export function formatDurationFromMonths(months: number | null | undefined): string {
  if (months == null) return ''
  if (months >= 12) {
    const years = Math.floor(months / 12)
    const remainder = months % 12
    const yearsPart = `${years} year${years !== 1 ? 's' : ''}`
    if (remainder) {
      return `${yearsPart} ${remainder} month${remainder !== 1 ? 's' : ''}`
    }
    return yearsPart
  }
  return `${months} month${months !== 1 ? 's' : ''}`
}

// ── Record ID formatting ────────────────────────────────
//
// Prefixed short IDs let users distinguish entity types at a glance.
// Format: PREFIX-XXXXXXXX (8-char uppercase UUID snippet).

export function formatRecordId(uuid: string | null | undefined, prefix: string): string {
  if (!uuid) return ''
  return `${prefix}-${uuid.slice(0, 8).toUpperCase()}`
}

export function formatClaimId(uuid: string | null | undefined): string {
  return formatRecordId(uuid, 'CLM')
}

export function formatHoldingId(uuid: string | null | undefined): string {
  return formatRecordId(uuid, 'REG')
}

// ── Registration reference ───────────────────────────────
//
// The canonical, human-facing reference for a registration (holding) is its
// assigned `registration_number` when present, otherwise a prefixed short
// UUID (REG-XXXXXXXX). This MUST stay byte-for-byte identical to the value
// the confirmation email/letter renders for `{{ registration.number }}`
// (see `backend/apps/communications/services/email_context/namespaces.py`
// `holding_scalars_ns`), so operators and homeowners can match an email to
// a record. Do not invent a separate numbering scheme here.
export function formatRegistrationReference(
  holding?: {
    registration_number?: string | null
    uuid?: string | null
  } | null,
): string {
  if (!holding) return ''
  const assigned = (holding.registration_number || '').trim()
  if (assigned) return assigned
  return formatHoldingId(holding.uuid)
}
